import { Box, Tooltip as MUITooltip, Stack, Typography } from "@mui/material";
import { grey } from "@mui/material/colors";
import type { ReactElement } from "react";
import { BatteryStatusIcon as NewBatteryStatusIcon } from "@/features/device-status-icons/battery-status-icon";
import { SignalStatusIcon as NewSignalStatusIcon } from "@/features/device-status-icons/signal-status-icon";
import {
    VerificationStatusIcon as NewVerificationStatusIcon,
    shouldShowSproutVerification,
} from "@/features/device-status-icons/verification-status-icon";
import { useTranslatedWarning } from "@/hooks/use-translated-warning";
import { composeDeviceAttributes } from "@/services/device-attributes/device-attributes";
import type MapEntityBase from "@/services/map-management/map-entities/map-entity-base";
import { getDisplayName } from "../../../../../services/device-attributes/attributes/general";
import { IRRIGATION_STATE } from "../../../../../services/device-attributes/attributes/irrigation/definitions";
import { getSproutVerificationState } from "../../../../../services/device-attributes/attributes/verification/getters";
import { doesDeviceVersionMatch, GENERATION_CODE } from "../../../../../services/device-attributes/versions-code";
import { disabledByNotes, makeWarnings } from "../../../../../services/map-management/map-entities/shared";
import { badgeColorMapping } from "../../configs/styles";
import { BADGE_VARIANT, WARNING_STATUS } from "../../configs/variants";
import { ValveStatus } from "./valve-status";

// Constants for tooltip content
const STATUS_TEXT_COLOR = "#fdfdfd";
const STATUS_TEXT_FONT_WEIGHT = 500;
const STATUS_TEXT_FONT_SIZE = 12;
const STATUS_TEXT_PADDING_X = 8;
const STATUS_TEXT_PADDING_Y = 6;

// Map warning status to badge variant, to resuse the colors from badgeColorMapping
const warningStatusToBadgeVariant: Record<WARNING_STATUS, BADGE_VARIANT> = {
    [WARNING_STATUS.ERROR]: BADGE_VARIANT.ERROR,
    [WARNING_STATUS.WARNING]: BADGE_VARIANT.WARNING,
};

const makeNotesDisabledText = (model: MapEntityBase["model"]) => {
    if (model?.notes) {
        return disabledByNotes(model.notes) ? "Disabled" : undefined;
    }
    return undefined;
};

function TooltipContent({ model }: { model: MapEntityBase["model"] }) {
    const title = getDisplayName({ model });
    const { warningToShow, status } = makeWarnings({ model, type: model.type });

    const { title: translatedWarningTitle } = useTranslatedWarning(warningToShow);
    // Catalogued titles are already title-cased; this only matters for the fallback
    // path (e.g. notes-derived "check notes").
    const capitalizedWarningTitle = translatedWarningTitle
        ? translatedWarningTitle.charAt(0).toUpperCase() + translatedWarningTitle.slice(1)
        : undefined;
    const statusText = capitalizedWarningTitle ?? makeNotesDisabledText(model) ?? undefined;
    const statusBgColor = status
        ? badgeColorMapping[warningStatusToBadgeVariant[status as WARNING_STATUS]].bg
        : grey[500];

    const {
        battery: { batteryLevel },
        network: { signalLevel },
        irrigation: { irrigationState },
    } = composeDeviceAttributes({ model });

    const showValveStatus = irrigationState !== IRRIGATION_STATE.CLOSED;

    // Depending on device generation we need to check different criteria at each step
    const isSproutDevice = doesDeviceVersionMatch(model, { generation: GENERATION_CODE.SPROUT });
    const isGen4Device = doesDeviceVersionMatch(model, { generation: GENERATION_CODE.GEN4 });

    /**
     * Only show verification for sprout devices or gen4 devices.
     *
     * As of Jun 9, 2025, despite the name indicating sprout verification, both sprout & gen4 devices
     * have the same model.sproutIrrigationVerification property and use the same getters.
     */
    const showVerification = isSproutDevice || isGen4Device ? shouldShowSproutVerification({ model }) : false;
    const verificationState = showVerification ? getSproutVerificationState({ model }) : undefined;

    // ThirdPartyDevices do NOT have the required data to display these statuses
    const showStatusIcons = model?.category !== "thirdPartyDevice";

    return (
        <Stack
            direction={"column"}
            spacing={1}
            sx={{
                width: "max-content",
                maxWidth: "200px",
            }}
        >
            {Boolean(statusText) && (
                <Typography
                    component={"div"}
                    style={{
                        borderTopLeftRadius: 3,
                        borderTopRightRadius: 3,
                    }}
                    sx={{
                        backgroundColor: statusBgColor,
                        px: `${STATUS_TEXT_PADDING_X}px`,
                        py: `${STATUS_TEXT_PADDING_Y}px`,
                        color: STATUS_TEXT_COLOR,
                        fontWeight: STATUS_TEXT_FONT_WEIGHT,
                        fontSize: `${STATUS_TEXT_FONT_SIZE}px`,
                    }}
                >
                    {statusText}
                </Typography>
            )}
            <Stack
                direction={"row"}
                spacing={6}
                justifyContent={"space-between"}
                alignItems={"center"}
                sx={{
                    px: `${STATUS_TEXT_PADDING_X}px`,
                    py: `${STATUS_TEXT_PADDING_Y}px`,
                    // the status buttons
                    "& div button.MuiButtonBase-root": {
                        height: "10px",
                        width: "10px",
                    },
                }}
            >
                <Typography
                    sx={{
                        color: "#222222",
                        fontSize: `${STATUS_TEXT_FONT_SIZE}px`,
                        fontWeight: STATUS_TEXT_FONT_WEIGHT,
                        overflowWrap: "break-word",
                        overflow: "hidden",
                    }}
                    component={"div"}
                >
                    {title}
                </Typography>
                {showStatusIcons && (
                    <Stack direction={"row"} alignItems={"center"} justifyContent={"center"} flex={1} gap={2}>
                        <NewBatteryStatusIcon batteryLevel={batteryLevel} includeLabel={false} includeTooltip={false} />
                        <NewSignalStatusIcon
                            lastContactTime={model.lastContact}
                            signalLevel={signalLevel}
                            includeLabel={false}
                            includeTooltip={false}
                        />
                        {showVerification && verificationState !== undefined && (
                            <div className={"mx-1 flex items-center justify-center"}>
                                <NewVerificationStatusIcon
                                    verificationState={verificationState}
                                    includeLabel={false}
                                    includeTooltip={false}
                                />
                            </div>
                        )}
                    </Stack>
                )}
            </Stack>
            {showValveStatus && (
                <Box
                    sx={{
                        borderTop: "1px solid #F1EFEF",
                        px: `${STATUS_TEXT_PADDING_X}px`,
                        py: `${STATUS_TEXT_PADDING_Y}px`,
                    }}
                >
                    <ValveStatus model={model} />
                </Box>
            )}
        </Stack>
    );
}

interface MarkerTooltipProps {
    placement?: "top" | "bottom" | "left" | "right";
    model?: MapEntityBase["model"];
    children: ReactElement;
    enabled?: boolean;
}

export function MarkerTooltip({ placement = "right", model, children, enabled = true }: MarkerTooltipProps) {
    return (
        <MUITooltip
            disableHoverListener={!enabled}
            disableFocusListener={true}
            disableTouchListener={true}
            title={<TooltipContent model={model} />}
            placement={placement}
            enterDelay={0}
            leaveDelay={0}
            disableInteractive
            slotProps={{
                popper: {
                    disablePortal: true,
                    sx: {
                        "& .MuiTooltip-tooltip": {
                            bgcolor: "background.paper",
                            color: "text.primary",
                            boxShadow: 1,
                            p: 0,
                        },
                        "& .MuiTooltip-arrow": {
                            color: "background.paper",
                        },
                    },
                },
            }}
        >
            {children}
        </MUITooltip>
    );
}
